import { useEffect, useImperativeHandle, useRef, forwardRef } from 'react';
import type { MonkeyWorldsWebViewRef } from '../../../src/quiz/hooks/useMonkeyWorldsGame';
import type { MonkeyWorldsAudioPayload, MonkeyWorldsStartPayload } from '../../../src/quiz/constants/monkeyWorlds.constants';

interface Props {
  html: string;
  onMessage: (event: { nativeEvent: { data: string } }) => void;
}

/**
 * Web host: the game runs in a same-origin `srcDoc` iframe. The game posts to `window.parent`; we
 * forward those as `{ nativeEvent: { data } }` so the hook's handler is platform-agnostic. Boot data
 * is applied by calling the iframe's exposed `__monkeyWorldsApplyStart` directly (srcDoc inherits
 * the parent origin).
 */
export const MonkeyWorldsWebView = forwardRef<MonkeyWorldsWebViewRef, Props>(function MonkeyWorldsWebView({ html, onMessage }, ref) {
  const frameRef = useRef<HTMLIFrameElement>(null);

  useImperativeHandle(ref, () => ({
    applyStart: (payload: MonkeyWorldsStartPayload) => {
      const win = frameRef.current?.contentWindow as (Window & { __monkeyWorldsApplyStart?: (p: MonkeyWorldsStartPayload) => void }) | null;
      win?.__monkeyWorldsApplyStart?.(payload);
    },
    applyAudio: (payload: MonkeyWorldsAudioPayload) => {
      const win = frameRef.current?.contentWindow as (Window & { __monkeyWorldsApplyAudio?: (p: MonkeyWorldsAudioPayload) => void }) | null;
      win?.__monkeyWorldsApplyAudio?.(payload);
    },
    reload: () => {
      // Re-running srcDoc reboots the runtime (re-posts `ready`).
      const el = frameRef.current;
      if (el) { const doc = el.srcdoc; el.srcdoc = ''; el.srcdoc = doc; }
    },
  }), []);

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.source !== frameRef.current?.contentWindow) return;
      if (typeof e.data !== 'string') return;
      onMessage({ nativeEvent: { data: e.data } });
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [onMessage]);

  return (
    <iframe
      ref={frameRef}
      title="Monkey Worlds"
      srcDoc={html}
      style={{ flex: 1, width: '100%', height: '100%', border: 'none', background: 'transparent' }}
    />
  );
});
